import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSavedMethods,
  initializePaymentSession,
} from "../../redux/slices/paymentLinkingSlice";
import { paymentLinkingApi } from "../services/paymentLinkingApi";
import {
  Avatar,
  CustomerDetails,
  ErrorState,
  LoadingState,
  PaymentSummary,
  PrimaryButton,
} from "../components";

/**
 * ShopeePay account linking panel (SNAP direct debit).
 *
 * Linking itself completes SYNCHRONOUSLY (no webhook to wait on for
 * the linking step; the backend call resolves immediately with
 * success/failure). But for `charge_immediately` plans the backend
 * also attempts the plan's first charge synchronously right after
 * linking, and ShopeePay can come back wanting the customer to
 * confirm THAT on their own checkout page (`web_redirect_url`) —
 * so there's a second, GoPay-style polling leg for that case:
 *
 *   Phase 1 — Pre-redirect:
 *     Tap CTA -> POST /shopeepay/initiate -> full-tab redirect to
 *     ShopeePay's authorization_url.
 *
 *   Phase 2 — Post-redirect (?from_shopeepay=1&authCode=...):
 *     ShopeePay bounces the browser back to /result (via
 *     PaymentResult's from_shopeepay relay, mirroring the GoPay
 *     pattern) with authCode + partnerReferenceNo + result, which
 *     forwards here with the same query string intact. We call
 *     completeShopeepayLinking() directly — no polling loop needed
 *     for the LINKING part. If that response carries a
 *     `web_redirect_url`, the plan's first charge needs the customer
 *     to confirm on ShopeePay's own page — redirect there instead of
 *     showing "Linked Successfully" (see Phase 2b).
 *
 *   Phase 2b — Charge-confirmation redirect back (?from_shopeepay_charge=1):
 *     After the customer confirms on ShopeePay's page, they're
 *     bounced back here (via AutoChargeHelper's return_url). The
 *     charge itself settles asynchronously via ShopeePay's
 *     debit-notify webhook, so we poll saved-methods for
 *     subscription_status.plan_status the same way GoPayLinking polls
 *     for an active instrument.
 *
 *   Phase 3 — Already-linked fast-path:
 *     Same mount-time saved-methods probe as GoPayLinking, so a
 *     customer who reaches this page after linking is already done
 *     (browser-back, bookmark, ...) sees the conclusive "Done" panel
 *     instead of the "Continue" CTA.
 */
const ShopeePayLinking = () => {
  const { linkCode } = useParams();
  const dispatch = useDispatch();

  const { paymentLink, loading: globalLoading, error } = useSelector((state) => ({
    paymentLink: state.paymentLinking.paymentLink,
    loading:
      state.paymentLinking.loading.details ||
      state.paymentLinking.loading.initialize,
    error: state.paymentLinking.error,
  }));

  const [initiating, setInitiating] = useState(false);
  const [initiateError, setInitiateError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [linkResult, setLinkResult] = useState(null); // { status: 'linked' | 'failed', message? }
  // True while the one-shot mount check (callback completion OR
  // already-linked probe) is in flight. Hides the CTA during this
  // window, same rationale as GoPayLinking's initialChecking.
  const [initialChecking, setInitialChecking] = useState(true);
  // True while polling for the first-charge's settled status after
  // the customer returns from ShopeePay's confirmation page (Phase 2b).
  const [confirmingCharge, setConfirmingCharge] = useState(false);
  const completingRef = useRef(false);
  const pollTimer = useRef(null);
  const pollAttempts = useRef(0);

  // Mount-time: (a) make sure we actually HAVE a session before doing
  // anything else, then (b) complete the ShopeePay callback if an
  // authCode is present in the URL, or (c) probe saved-methods for
  // an already-linked instrument (browser-back / bookmark / re-mount
  // case, same as GoPayLinking).
  //
  // The session bootstrap and the callback-completion used to be two
  // independent effects racing each other. That's fine for a normal
  // SPA navigation into this page, but after the ShopeePay redirect
  // round-trip the browser does a FULL page reload — paymentLinkingApi's
  // in-memory sessionToken is wiped — so if completeShopeepayLinking()
  // fired before initializePaymentSession() finished setting the new
  // token, it went out unauthenticated and the customer saw "Session
  // expired" despite ShopeePay having authorized successfully. We now
  // always await session readiness first.
  useEffect(() => {
    let cancelled = false;

    const ensureSession = async () => {
      if (paymentLinkingApi.sessionToken) return;
      try {
        await dispatch(initializePaymentSession({ linkCode })).unwrap();
      } catch (err) {
        console.error("[ShopeePayLinking] session init failed", err);
      }
    };

    const runCallback = async (authCode, partnerReferenceNo, shopeepayResult) => {
      if (completingRef.current) return;
      completingRef.current = true;
      setCompleting(true);
      try {
        const response = await paymentLinkingApi.completeShopeepayLinking(linkCode, {
          authCode,
          partnerReferenceNo,
          result: shopeepayResult,
        });
        if (cancelled) return;

        const webRedirectUrl = response?.data?.web_redirect_url;
        if (webRedirectUrl) {
          // Linking succeeded, but the plan's first charge needs the
          // customer to confirm on ShopeePay's own page. Redirect
          // immediately rather than showing "Linked Successfully" and
          // stranding that charge — full-tab redirect, same as the
          // authorization step. They'll land back at
          // ?from_shopeepay_charge=1 once done (see Phase 2b above).
          window.location.href = webRedirectUrl;
          return;
        }

        setLinkResult({ status: "linked" });
      } catch (err) {
        if (cancelled) return;
        console.error("[ShopeePayLinking] complete failed", err);
        setLinkResult({
          status: "failed",
          message:
            err?.message ||
            "Failed to complete ShopeePay linking. Please try again.",
        });
      } finally {
        if (!cancelled) {
          setCompleting(false);
          setInitialChecking(false);
        }
      }
    };

    const runInitialCheck = async () => {
      await ensureSession();
      if (cancelled) return;

      const params = new URLSearchParams(window.location.search);
      const authCode = params.get("authCode");
      const shopeepayResult = params.get("result");

      // Customer just confirmed the plan's first charge on ShopeePay's
      // own page (Phase 2b) and bounced back via AutoChargeHelper's
      // return_url. The charge settles asynchronously via ShopeePay's
      // debit-notify webhook, so poll for it rather than assuming
      // success just because they made it back here.
      if (params.get("from_shopeepay_charge") === "1") {
        startPollingForChargeSettlement();
        return;
      }

      // Customer just bounced back from ShopeePay with an authCode —
      // complete linking directly, no polling needed.
      if (params.get("from_shopeepay") === "1" && authCode) {
        // ShopeePay's documented result codes: 100 = success, 201 =
        // binding failed.
        if (shopeepayResult && shopeepayResult !== "100") {
          setLinkResult({
            status: "failed",
            message: "ShopeePay account linking was not completed.",
          });
          setInitialChecking(false);
          return;
        }
        await runCallback(
          authCode,
          params.get("partnerReferenceNo"),
          shopeepayResult
        );
        return;
      }

      // Otherwise: probe saved-methods for an already-linked
      // instrument (same rationale as GoPayLinking's mount-time
      // probe — covers browser-back, bookmark, re-mount).
      try {
        const result = await dispatch(fetchSavedMethods(linkCode)).unwrap();
        if (cancelled) return;
        const methods = result?.saved_methods || [];
        const activeShopeepay = methods.find(
          (m) =>
            m.type === "ewallet" &&
            m.provider === "shopeepay" &&
            m.status === "active"
        );
        if (activeShopeepay) {
          setLinkResult({ status: "linked", instrument: activeShopeepay });
        }
        setInitialChecking(false);
      } catch (err) {
        if (cancelled) return;
        console.error(
          "[ShopeePayLinking] initial saved-methods probe failed",
          err
        );
        setInitialChecking(false);
      }
    };

    runInitialCheck();
    return () => {
      cancelled = true;
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for the plan's first-charge settlement after the customer
  // returns from ShopeePay's confirmation page. Mirrors GoPayLinking's
  // startPollingForLinkedInstrument, but keys off subscription_status
  // .plan_status (PENDING_PAYMENT -> ACTIVE) instead of instrument
  // status, since the instrument here is already ACTIVE regardless of
  // whether this specific charge succeeds.
  const startPollingForChargeSettlement = () => {
    if (pollTimer.current) return;
    setConfirmingCharge(true);
    setInitialChecking(false);
    pollAttempts.current = 0;

    const tick = async () => {
      pollAttempts.current += 1;
      try {
        const result = await dispatch(fetchSavedMethods(linkCode)).unwrap();
        const planStatus = result?.subscription_status?.plan_status;

        if (planStatus === "active") {
          setLinkResult({ status: "linked" });
          stopPolling();
          return;
        }

        if (planStatus === "cancelled") {
          setLinkResult({
            status: "failed",
            message:
              "Your ShopeePay payment could not be confirmed and the subscription was cancelled. Please start over.",
          });
          stopPolling();
          return;
        }

        // Stop after ~2 minutes (40 attempts at 3s each) — still
        // PENDING_PAYMENT with no terminal signal either way.
        if (pollAttempts.current >= 40) {
          setLinkResult({
            status: "pending",
            message:
              "Still waiting on confirmation from ShopeePay. You can close this page — the subscription will start as soon as your payment is confirmed.",
          });
          stopPolling();
        }
      } catch (err) {
        console.error("[ShopeePayLinking] charge settlement poll error", err);
      }
    };

    tick();
    pollTimer.current = setInterval(tick, 3000);
  };

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    setConfirmingCharge(false);
  };

  const handleLinkShopeepay = async () => {
    // Defensive double-click / re-entry guard, same as GoPayLinking.
    if (initiating || completing || linkResult?.status === "linked") {
      return;
    }
    setInitiateError(null);
    setInitiating(true);
    // Points at /result (not directly at /shopeepay) so the return
    // trip goes through PaymentResult's from_shopeepay relay — same
    // indirection GoPayLinking uses for from_gopay, kept consistent
    // rather than special-cased.
    const resultUrl = `${window.location.origin}/b2b/linking/${linkCode}/result?from_shopeepay=1`;
    try {
      const response = await paymentLinkingApi.initiateShopeepayLinking(
        linkCode,
        { redirectUrl: resultUrl }
      );
      const data = response?.data || {};
      const authorizationUrl = data.authorization_url;
      if (!authorizationUrl) {
        throw new Error("Backend did not return a ShopeePay authorization URL");
      }
      // Full-tab redirect to ShopeePay's authorization page.
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error("[ShopeePayLinking] initiate failed", err);
      setInitiateError(
        err?.message || "Failed to start ShopeePay linking. Please try again."
      );
      setInitiating(false);
    }
  };

  if (!paymentLink && globalLoading) {
    return <LoadingState />;
  }

  if (error && !paymentLink) {
    return <ErrorState error={error} />;
  }

  if (!paymentLink) {
    return null;
  }

  const { merchant, customer, items, pricing } = paymentLink;

  const isLinked = linkResult?.status === "linked";

  const merchantRedirectUrl = paymentLink?.settings?.redirect_url || null;
  const handleDoneClick = () => {
    if (merchantRedirectUrl) {
      window.location.href = merchantRedirectUrl;
      return;
    }
    window.close();
  };

  const renderLinkedDonePanel = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{ backgroundColor: "#ECFDF5" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="#01793B"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-lg sm:text-xl font-bold mb-2" style={{ color: "#3B3B3B" }}>
        ShopeePay Linked Successfully
      </h1>
      <p className="text-xs sm:text-sm mb-6" style={{ color: "#515164" }}>
        Your ShopeePay account is linked to this subscription. Future bills
        will be charged automatically on schedule — there's nothing else for
        you to do here.
      </p>
      <PrimaryButton onClick={handleDoneClick}>
        {merchantRedirectUrl ? "Return to Merchant" : "Done"}
      </PrimaryButton>
    </div>
  );

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ backgroundColor: "#F9FAFC" }}
    >
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Avatar name={merchant?.name} imageUrl={merchant?.logo_url} size="md" />
          <h1 className="text-lg sm:text-xl font-bold" style={{ color: "#3B3B3B" }}>
            {merchant?.name || "Merchant"}
          </h1>
        </div>

        {isLinked ? (
          renderLinkedDonePanel()
        ) : (
          <>
            <div className="mb-6">
              <h2
                className="text-xl sm:text-2xl font-bold mb-1"
                style={{ color: "#3B3B3B" }}
              >
                Link Your ShopeePay
              </h2>
              <p className="text-xs sm:text-sm" style={{ color: "#515164" }}>
                We'll link your ShopeePay account so future bills can be
                charged automatically. You can unlink it any time.
              </p>
            </div>

            <CustomerDetails customer={customer} />

            <PaymentSummary items={items} pricing={pricing} />

            <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
              <p className="text-sm font-semibold text-slate-800 mb-1">
                Pay with ShopeePay
              </p>
              <p className="text-xs text-slate-600">
                Tapping continue will open ShopeePay to authorize the link.
                You'll be redirected back here automatically.
              </p>
            </div>

            {completing && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6">
                <p className="text-sm font-medium text-amber-900">
                  Confirming your ShopeePay link…
                </p>
              </div>
            )}

            {confirmingCharge && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-6">
                <p className="text-sm font-medium text-amber-900">
                  Confirming your ShopeePay payment…
                </p>
              </div>
            )}

            {linkResult?.status === "failed" && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4">
                <p className="text-sm text-red-800">{linkResult.message}</p>
              </div>
            )}

            {linkResult?.status === "pending" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4">
                <p className="text-sm text-slate-700">{linkResult.message}</p>
              </div>
            )}

            {initiateError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4">
                <p className="text-sm text-red-800">{initiateError}</p>
              </div>
            )}

            {!initialChecking &&
              !completing &&
              !confirmingCharge &&
              linkResult?.status !== "pending" && (
                <PrimaryButton onClick={handleLinkShopeepay} disabled={initiating}>
                  {initiating ? "Opening ShopeePay…" : "Continue to ShopeePay"}
                </PrimaryButton>
              )}
            {initialChecking && !confirmingCharge && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                <p className="text-sm text-slate-700">
                  Checking your ShopeePay link status…
                </p>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-4">
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            © 2025 {process.env.REACT_APP_NAME || "SingaEngine"}. All Rights
            Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShopeePayLinking;
